// ─── Vue MOIS : prévu vs réalisé ──────────────────────────────────
// Le livrable central (cf. CONCEPTION-FINANCE.md §6). Pour un mois donné,
// globalement ou par enveloppe : ce qui était prévu, ce qui a eu lieu, l'écart,
// ce qui manque, et les IMPRÉVUS (actuals rattachés à rien).
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { LigneSwipe, useUI, useT } from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import {
  occurrencesDuMois, imprevusDuMois, internesDuMois, decalerMois,
  moisCourant, moisVoisinAvecPrevu,
} from './services/occurrences';
import { filtrerOccurrences, filtrerImprevus } from './services/filtres';
import { emojiCategorie, emojiBudget } from './data/categories';
import { STATUTS, estClos } from './data/statuts';
import EnTeteArbre, { RETRAIT_ENFANTS } from './EnTeteArbre';


const libelleMois = (mk, t) => {
  const [y, m] = String(mk).split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk} ${y}`;
};

// Gestes standard de l'app sur un élément : swipe ← supprimer · swipe → éditer
// (+ ⇄ « mouvement » pour les autres actions) · tap = fiche INFO en lecture.
export default function VueMois({
  mois, lignes = [], actuals = [], exceptions = [], budgets = [], rattachements = null,
  budgetIds = [], recherche = '', tri = 'statut',
  onMois, onOccurrence, onEditerLigne, onSupprimerLigne, onEtatOccurrence,
  onImprevu, onRattacher, onSupprimerImprevu,
  // La barre des mois peut être rendue PAR L'ÉCRAN, hors défilement
  // (Cédric, 16/08) : `sansBarre` masque alors celle-ci.
  sansBarre = false,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  // On calcule TOUT le mois, puis on filtre/trie l'affichage. Les totaux, eux,
  // sont ceux du PÉRIMÈTRE FILTRÉ (enveloppes) mais ignorent la recherche : un
  // total qui changerait à chaque lettre tapée n'aurait aucun sens.
  const occurrencesTout = useMemo(
    () => occurrencesDuMois({ lignes, actuals, exceptions, rattachements, mk: mois }),
    [lignes, actuals, exceptions, rattachements, mois],
  );
  // Un imprévu est désormais ce à quoi il RESTE quelque chose à affecter : une
  // opération peut être rattachée et garder une part qui n'honore aucune
  // prévision. C'est cette part qui manque, pas l'opération entière.
  const imprevusTout = useMemo(
    () => imprevusDuMois({ actuals, rattachements, mk: mois }),
    [actuals, rattachements, mois],
  );
  const internes = useMemo(() => internesDuMois({ actuals, mk: mois }), [actuals, mois]);

  const occurrences = useMemo(
    () => filtrerOccurrences(occurrencesTout, { recherche, budgetIds, tri }),
    [occurrencesTout, recherche, budgetIds, tri],
  );
  const imprevus = useMemo(
    () => filtrerImprevus(imprevusTout, { recherche, budgetIds, tri }),
    [imprevusTout, recherche, budgetIds, tri],
  );

  const surCourant = mois === moisCourant();
  const sautAvant = useMemo(() => moisVoisinAvecPrevu(lignes, mois, -1), [lignes, mois]);
  const sautApres = useMemo(() => moisVoisinAvecPrevu(lignes, mois, 1), [lignes, mois]);
  const nomBudget = (id) => (budgets.find(b => b.id === id) || {}).nom || '';

  // Les groupes REPLIÉS. Ouverts par défaut : ce qui est neuf doit se voir,
  // le pliage sert à ranger ce qu'on a fini de lire (leçon du 14/08).
  // Les GROUPES de budgets sont repliés d’emblée : « je veux avoir les écrans
  // épurés » (Cédric, 15/08). Ce qu’on déplie, en revanche, s’affiche en
  // entier — le pliage range, il ne cache pas.
  const [replies, setReplies] = useState(null);   // null = pas encore initialisé
  const [impPlie, setImpPlie] = useState(true);
  const [intPlie, setIntPlie] = useState(true);

  // La somme de chaque section : un titre replié doit dire ce qu’il cache,
  // sinon replier revient à perdre l’information.
  // Somme SIGNEE : les revenus s'ajoutent, les depenses se retranchent.
  // Avec Math.abs, un virement recu de 5 000 € et un envoye de 5 000 €
  // faisaient 10 000 € — un total qui ne correspond a rien (Cedric, 15/08 :
  // « il y a un −5000 donc pas 24k mais 14k »).
  const signee = (ops) => ops.reduce(
    (n, op) => n + (op.sens === 'revenu' ? 1 : -1) * Math.abs(op.montant || 0), 0);
  const sommeImprevus = useMemo(() => signee(imprevus), [imprevus]);
  const sommeInternes = useMemo(() => signee(internes), [internes]);

  // Groupes = budgets, dans leur ordre ; les prévisions sans budget en
  // dernier — c'est un reste à ranger, pas une catégorie.
  const groupes = useMemo(() => {
    const par = new Map();
    for (const o of occurrences) {
      const cle = o.ligne?.budgetId || null;
      if (!par.has(cle)) par.set(cle, []);
      par.get(cle).push(o);
    }
    const ordre = [...budgets.map(b => b.id), null].filter(c => par.has(c));
    return ordre.map(cle => ({
      cle,
      nom: nomBudget(cle),
      // L'emoji du budget — le Suivi était le seul arbre sans (15/08).
      emoji: cle ? emojiBudget(budgets.find(b => b.id === cle)) : '❓',
      occ: par.get(cle),
      // Le total du groupe : prévu et réel, comme les lignes qu'il coiffe.
      // SIGNÉ (15/08) : un débit prévu de 1 000 € non rattaché donne un écart
      // de +1 000 € — on a dépensé MOINS que prévu, c'est favorable. Un crédit
      // manquant donne −1 000 €. La couleur devient directe.
      prevu: par.get(cle).reduce((n, o) => n + (o.prevuSigne || 0), 0),
      reel: par.get(cle).reduce((n, o) => n + (o.reelSigne || 0), 0),
    }));
  }, [occurrences, budgets]);

  // Au premier rendu, TOUT est replié ; ensuite on respecte les choix.
  const groupesReplies = replies ?? new Set(groupes.map(g => g.cle));
  const basculer = (cle) => setReplies((prev) => {
    const base = prev ?? new Set(groupes.map(g => g.cle));
    const n = new Set(base);
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    return n;
  });
  return (
    <View>
      {/* Navigation entre les mois. « Mois courant » = icône 🎯 collée au titre,
          comme dans My Calendar : toujours présente, grisée quand on y est déjà,
          pour que l'alignement du titre ne bouge jamais. */}
      {!sansBarre && <View style={s.barre}>
        {/* « » sautent au mois voisin qui porte des échéances (Cédric,
            15/08) — même principe que le relevé de compte : un budget qui
            commence loin se traverse sinon mois vide par mois vide. */}
        <TouchableOpacity onPress={() => sautAvant && onMois?.(sautAvant)} hitSlop={HIT}
          disabled={!sautAvant}>
          <Text style={[s.nav, !sautAvant && { opacity: 0.28 }]}>«</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => onMois?.(decalerMois(mois, -1))} hitSlop={HIT}>
          <Text style={s.nav}>‹</Text>
        </TouchableOpacity>
        <View style={s.titreZone}>
          <Text style={s.mois}>{libelleMois(mois, t)}</Text>
          <TouchableOpacity onPress={() => onMois?.(moisCourant())} disabled={surCourant} hitSlop={HIT}
            accessibilityLabel={t('finances.moisCourant')}>
            <Text style={[s.aujIc, surCourant && { opacity: 0.28 }]}>🎯</Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity onPress={() => onMois?.(decalerMois(mois, 1))} hitSlop={HIT}>
          <Text style={s.nav}>›</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => sautApres && onMois?.(sautApres)} hitSlop={HIT}
          disabled={!sautApres}>
          <Text style={[s.nav, !sautApres && { opacity: 0.28 }]}>»</Text>
        </TouchableOpacity>
      </View>}

      {/* La SYNTHÈSE ne vit plus ici : `SyntheseMois` est rendue par l'écran,
          dans l'en-tête FIXE, au-dessus de la recherche (Cédric, 16/08). */}
      {occurrences.length === 0 && imprevus.length === 0 && (
        <Text style={s.vide}>{t('finances.moisVide')}</Text>
      )}

      {groupes.map(g => {
        const plie = groupesReplies.has(g.cle);
        return (
        <View key={String(g.cle)}>
          {/* Un seul budget filtré : le groupe n'apprendrait rien, tout lui
              appartient. On l'efface plutôt que d'afficher un intitulé vide
              de sens. */}
          {budgetIds.length !== 1 && (
            /* Grammaire commune (15/08) : rangée plate, chevron à gauche,
               emoji + nom + effectif, la donnée clé à droite. Réduite à
               l'ÉCART seul (Cédric, 17/08) : « réel / prévu » au complet
               vit désormais en pied de groupe, visible seulement une fois
               déplié — le chiffre qui reste toujours visible, replié ou
               pas, est celui qui répond le plus vite à « ça va ou pas ? ». */
            <EnTeteArbre plie={plie} onBasculer={() => basculer(g.cle)}
              emoji={g.emoji} titre={g.nom || t('budget.sansBudget')} nb={g.occ.length}
              droite={(
                <Text style={[s.groupeEcart, {
                  color: g.reel - g.prevu > 0.005 ? (UI.ok || '#2e7d32')
                    : g.reel - g.prevu < -0.005 ? (UI.danger || '#c0392b') : UI.muted,
                }]}>
                  {g.reel - g.prevu > 0 ? '+' : ''}{formatEuro(g.reel - g.prevu)}
                </Text>
              )} />
          )}
          {/* « réel / prévu (écart) » — ce que portait l'en-tête avant,
              reporté TEL QUEL ici, AVANT les lignes du groupe (Cédric,
              17/08 : « plutôt que après »). Visible seulement déplié :
              replié, c'est l'écart de l'en-tête qui répond déjà. */}
          {!plie && budgetIds.length !== 1 && g.occ.length > 0 && (
            <View style={s.groupeSynthese}>
              <Text style={s.groupeTotal}>
                {formatEuro(g.reel)} <Text style={s.groupePrevu}>/ {formatEuro(g.prevu)}</Text>
                {Math.abs(g.reel - g.prevu) >= 0.01 && (
                  <Text style={{ color: g.reel - g.prevu > 0 ? (UI.ok || '#2e7d32') : (UI.danger || '#c0392b'), fontWeight: '700' }}>
                    {'  ('}{g.reel - g.prevu > 0 ? '+' : ''}{formatEuro(g.reel - g.prevu)}{')'}
                  </Text>
                )}
              </Text>
            </View>
          )}
          <View style={budgetIds.length !== 1 ? { marginLeft: RETRAIT_ENFANTS } : null}>
          {(plie && budgetIds.length !== 1 ? [] : g.occ).map(o => {
        const st = STATUTS[o.statut] || STATUTS.avenir;
        const raye = o.statut === 'absente' || o.statut === 'ignoree';
        const clos = estClos(o);
        return (
        <LigneSwipe key={o.ligneId} espacement={8} enabled
          onSupprimer={() => onSupprimerLigne?.(o)}
          onEditer={() => onEditerLigne?.(o)}
          /* ⇄ ouvrait la même fiche que le tap (Cédric, 17/08, signalé) : ce
             geste bascule maintenant l'état SOLDÉE — même icône/libellé/
             action que le bouton de `FicheOccurrence` (« rien de plus » ↔
             « rouvrir »), pas un doublon inventé ici. */
          onDeplacer={() => onEtatOccurrence?.(o)}
          iconeDeplacer={clos ? '✅' : '☑️'}
          labelDeplacer={t(clos ? 'finances.rouvrirMois' : 'finances.rienDePlusMois')}
          couleurDeplacer={clos ? undefined : (UI.ok || '#2e7d32')}>
          <TouchableOpacity style={s.ligne} activeOpacity={0.7} onPress={() => onOccurrence?.(o)}>
            <Text style={{ fontSize: 17 }}>{st.emoji}</Text>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[s.libelle, raye && s.raye]} numberOfLines={1}>
                {emojiCategorie(o.ligne.categorie)} {o.ligne.libelle}
              </Text>
              <Text style={s.sous} numberOfLines={1}>
                {!budgetIds.length && nomBudget(o.ligne.budgetId) ? `${nomBudget(o.ligne.budgetId)}  ·  ` : ''}
                {t(st.cle)}
                {o.nb > 0 ? `  ·  ${o.nb}` : ''}
              </Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              {/* Signé comme partout ailleurs (convention `montantOperation`,
                  Cédric 15/08 réaffirmée) : le débit porte « − », le crédit
                  « + ». Reconstruit à la main plutôt qu'appelé deux fois — le
                  signe est le même pour le réel et le prévu d'une même ligne. */}
              <Text style={[s.montants, o.ligne.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
                {o.ligne.sens === 'revenu' ? '+ ' : '− '}{formatEuro(o.reel)}
                <Text style={s.prevu}> / {o.ligne.sens === 'revenu' ? '+ ' : '− '}{formatEuro(o.prevu)}</Text>
              </Text>
              {o.statut !== 'avenir' && Math.abs(o.ecart) > 0.005 && (
                <Text style={[s.ecart, { color: o.ecart < 0 ? UI.muted : (UI.danger || '#c0392b') }]}>
                  {o.ecart < 0 ? t('finances.reste') : t('finances.depasse')} {formatEuro(Math.abs(o.ecart))}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        </LigneSwipe>
        );
          })}
          </View>
        </View>
        );
      })}

      {imprevus.length > 0 && (
        <>
          <TouchableOpacity style={s.section} activeOpacity={0.7}
            onPress={() => setImpPlie(v => !v)}>
            <Text style={s.sectionChev}>{impPlie ? '▸' : '▾'}</Text>
            <Text style={s.titreSection}>❓ {t('finances.imprevus')}</Text>
            <Text style={s.sectionNb}>{imprevus.length}</Text>
            <View style={{ flex: 1 }} />
            <Text style={[s.sectionSomme, sommeImprevus > 0 && { color: UI.ok || '#2e7d32' },
              sommeImprevus < 0 && { color: UI.danger || '#c0392b' }]}>
              {sommeImprevus > 0 ? '+ ' : ''}{formatEuro(sommeImprevus)}
            </Text>
          </TouchableOpacity>
          {(impPlie ? [] : imprevus).map(op => (
            // Un imprévu est une OPÉRATION réelle : ⇄ le rattache à une ligne
            // prévue (son action principale), 🗑️ le supprime.
            <LigneSwipe key={op.id} espacement={8} enabled
              onSupprimer={() => onSupprimerImprevu?.(op)}
              onDeplacer={() => onRattacher?.(op)}>
              <TouchableOpacity style={s.ligne} activeOpacity={0.7} onPress={() => onImprevu?.(op)}>
                <Text style={{ fontSize: 17 }}>❓</Text>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.libelle} numberOfLines={1}>{op.libelle || emojiCategorie(op.categorie)}</Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {!budgetIds.length && nomBudget(op.budgetId) ? `${nomBudget(op.budgetId)}  ·  ` : ''}
                    {t('finances.nonPrevu')}
                  </Text>
                </View>
                <Text style={[s.montants, op.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
                  {montantOperation(op.montant, op.sens)}
                </Text>
              </TouchableOpacity>
            </LigneSwipe>
          ))}
        </>
      )}

      {/* Hors calcul : ces opérations ne pèsent dans aucun total, mais rester
          listées est indispensable — sinon on ne peut plus les réintégrer. */}
      {internes.length > 0 && (
        <>
          <TouchableOpacity style={s.section} activeOpacity={0.7}
            onPress={() => setIntPlie(v => !v)}>
            <Text style={s.sectionChev}>{intPlie ? '▸' : '▾'}</Text>
            <Text style={s.titreSection}>⊘ {t('finances.horsCalcul')}</Text>
            <Text style={s.sectionNb}>{internes.length}</Text>
            <View style={{ flex: 1 }} />
            <Text style={[s.sectionSomme, sommeInternes > 0 && { color: UI.ok || '#2e7d32' },
              sommeInternes < 0 && { color: UI.danger || '#c0392b' }]}>
              {sommeInternes > 0 ? '+ ' : ''}{formatEuro(sommeInternes)}
            </Text>
          </TouchableOpacity>
          {(intPlie ? [] : internes).map(op => (
            <LigneSwipe key={op.id} espacement={8} enabled
              onSupprimer={() => onSupprimerImprevu?.(op)}
              onDeplacer={() => onRattacher?.(op)}>
              <TouchableOpacity style={[s.ligne, { opacity: 0.55 }]} activeOpacity={0.7}
                onPress={() => onImprevu?.(op)}>
                <Text style={{ fontSize: 17 }}>⊘</Text>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.libelle} numberOfLines={1}>{op.libelle || emojiCategorie(op.categorie)}</Text>
                  <Text style={s.sous} numberOfLines={1}>{t('finances.interne')}</Text>
                </View>
                <Text style={s.montants}>{montantOperation(op.montant, op.sens)}</Text>
              </TouchableOpacity>
            </LigneSwipe>
          ))}
        </>
      )}
    </View>
  );
}

const HIT = { top: 10, bottom: 10, left: 10, right: 10 };

const makeStyles = (UI) => StyleSheet.create({
  barre: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 },
  nav: { fontSize: 26, color: UI.accent, fontWeight: '300', width: 44, textAlign: 'center' },
  titreZone: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  mois: { fontSize: 16, fontWeight: '800', color: UI.ink },
  aujIc: { fontSize: 15 },
  reste: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 13, marginBottom: 10 },
  resteLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 2 },
  resteLbl: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  resteVal: { fontSize: 16, fontWeight: '800', color: UI.ink },
  // ⚠️ COLONNE, pas rangée : ce conteneur alignait les trois anciens blocs
  // côte à côte. Resté en `row` après le passage au tableau (0.6.54.0), il
  // compressait les quatre lignes en largeur nulle — carte VIDE à l'écran.
  totaux: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginTop: 6, marginBottom: 10 },
  ligneSynthese: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3 },
  ligneTotal: { borderTopWidth: 0.5, borderTopColor: UI.border, marginTop: 3, paddingTop: 6 },
  colDroite: { flex: 1.2, textAlign: 'right' },
  // Colonne des libellés RESSERRÉE (Cédric, 15/08) : « Crédits » n'a pas
  // besoin d'autant qu'une colonne de montants — à largeur égale, c'est le
  // « € » du total qui passait à la ligne. Les corps de texte baissent d'un
  // demi-point pour la même raison : trois lignes, jamais quatre.
  colGauche: { flex: 0.7 },
  syntheseLbl: { flex: 0.7, fontSize: 12.5, color: UI.muted, fontWeight: '700' },
  syntheseVal: { flex: 1.2, fontSize: 12.5, color: UI.ink, fontWeight: '700', textAlign: 'right' },
  syntheseTot: { flex: 1.2, fontSize: 13, color: UI.ink, fontWeight: '800', textAlign: 'right' },
  bloc: { flex: 1 },
  blocLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  blocVal: { fontSize: 15, fontWeight: '800', color: UI.ink, marginTop: 2 },
  // PAS de marginBottom ici : l'espacement vient de `LigneSwipe` (prop
  // `espacement`). Une marge sur la carte ferait dépasser les boutons de swipe,
  // qui épousent la hauteur totale de la ligne, marge comprise.
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  groupe: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 9, paddingHorizontal: 4, marginTop: 6 },
  section: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 4, marginTop: 10 },
  sectionChev: { fontSize: 13, color: UI.accent, fontWeight: '800', width: 14 },
  titreSection: { fontSize: 13, color: UI.faint, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3 },
  sectionNb: { fontSize: 12, color: UI.faint, fontWeight: '700' },
  sectionSomme: { fontSize: 13.5, fontWeight: '800', color: UI.muted },
  groupeChev: { fontSize: 13, color: UI.accent, fontWeight: '800', width: 14 },
  groupeNom: { fontSize: 14.5, fontWeight: '800', color: UI.ink, maxWidth: '45%' },
  groupeNb: { fontSize: 12, color: UI.faint, fontWeight: '700' },
  groupeTotal: { fontSize: 13, fontWeight: '800', color: UI.muted },
  groupePrevu: { fontWeight: '700', color: UI.faint },
  groupeEcart: { fontSize: 13.5, fontWeight: '800' },
  groupeSynthese: { alignItems: 'flex-end', paddingRight: 4, marginTop: -2, marginBottom: 6 },
  libelle: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  raye: { textDecorationLine: 'line-through', color: UI.muted },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  montants: { fontSize: 14, fontWeight: '800', color: UI.ink },
  prevu: { fontSize: 12, fontWeight: '600', color: UI.faint },
  ecart: { fontSize: 11.5, fontWeight: '700', marginTop: 1 },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 8, marginBottom: 6 },
  vide: { fontSize: 13.5, color: UI.faint, textAlign: 'center', paddingVertical: 24, lineHeight: 19 },
});
